// The bells page's sound: a handbell made in the browser (no samples to fetch) and a
// quiet wooden click, behind the practice player's transport (practice-core.js).
// A handbell is tuned to its fundamental and the twelfth above it, with a few weaker,
// inharmonic partials that die away first; a bell rings for its written length and is
// then damped. Bells sound an octave above where they are written.
import type { TransportAudio } from './practice-core';

const PARTIALS: [number, number][] = [[1, 1], [3, 0.3], [2, 0.05], [4.2, 0.05], [5.42, 0.08], [6.8, 0.03]];

export class BellAudio implements TransportAudio {
  context: AudioContext;
  private out: GainNode;
  // One volume for each part, so that what plays can change while the music runs: the
  // notes already queued for the next bars go through it too (Jack, 2026-11-03).
  private levels = new Map<string, number>();
  private gains = new Map<string, GainNode>();
  private voices = new Set<{ stop: (t: number) => void; end: number }>();
  private noise: AudioBuffer;

  constructor() {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.context = new AC({ latencyHint: 'interactive' });
    const comp = this.context.createDynamicsCompressor();
    comp.threshold.value = -16; comp.ratio.value = 3; comp.attack.value = 0.003; comp.release.value = 0.25;
    this.out = this.context.createGain();
    this.out.gain.value = 0.7;
    this.out.connect(comp); comp.connect(this.context.destination);
    const n = Math.floor(this.context.sampleRate * 0.03);
    this.noise = this.context.createBuffer(1, n, this.context.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  }

  private partGain(part: string) {
    let g = this.gains.get(part);
    if (!g) { g = this.context.createGain(); g.gain.value = this.levels.get(part) ?? 0.8; g.connect(this.out); this.gains.set(part, g); }
    return g;
  }
  setLevel(part: string, level: number) {
    this.levels.set(part, level);
    const g = this.gains.get(part);
    if (g) { const t = this.context.currentTime; g.gain.cancelScheduledValues(t); g.gain.setTargetAtTime(level, t, 0.03); }
  }
  lead() { return 0.08; }
  latency() { const c = this.context as AudioContext & { outputLatency?: number }; return (c.outputLatency || 0) + (c.baseLatency || 0); }
  begin() { if (this.context.state === 'suspended') this.context.resume(); }

  note(part: string, midi: number, at: number, duration: number, velocity: number) {
    // Every part is queued, heard or not; its volume decides whether it is heard.
    this.ring(midi + 12, at, duration, 0.55 + 0.45 * (velocity || 0.7), this.partGain(part));
  }

  // A bell rung now, by a key or a thumb.
  strike(midi: number, level = 0.9) { this.begin(); this.ring(midi + 12, this.context.currentTime + 0.005, 1.2, level); }

  ring(midi: number, at: number, duration: number, level: number, dest: AudioNode = this.out) {
    const ctx = this.context, f = 440 * Math.pow(2, (midi - 69) / 12);
    const tau = Math.max(0.45, Math.min(2.6, 1.7 * Math.sqrt(1046 / f)));
    const damp = ctx.createGain(); damp.gain.value = 1; damp.connect(dest);
    const oscs: OscillatorNode[] = [];
    for (const [ratio, amp] of PARTIALS) {
      if (f * ratio > 16000) continue;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f * ratio;
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(0.22 * amp * level, at + 0.004);
      g.gain.setTargetAtTime(0, at + 0.004, tau / (1 + 0.7 * (ratio - 1)));
      o.connect(g); g.connect(damp); o.start(at); oscs.push(o);
    }
    const s = ctx.createBufferSource(), bp = ctx.createBiquadFilter(), sg = ctx.createGain();
    s.buffer = this.noise; bp.type = 'bandpass'; bp.frequency.value = Math.min(9000, f * 4); bp.Q.value = 3;
    sg.gain.value = 0.05 * level; s.connect(bp); bp.connect(sg); sg.connect(damp); s.start(at);
    const end = at + Math.max(0.15, duration);
    damp.gain.setTargetAtTime(0, end, 0.05);
    const stopAt = end + 0.4;
    for (const o of oscs) o.stop(stopAt);
    const voice = {
      end: stopAt,
      stop: (t: number) => { damp.gain.cancelScheduledValues(t); damp.gain.setTargetAtTime(0, t, 0.02); for (const o of oscs) { try { o.stop(t + 0.15); } catch { /* already stopped */ } } },
    };
    this.voices.add(voice);
    oscs[0]?.addEventListener('ended', () => this.voices.delete(voice));
  }

  click(at: number, tier: number, count: number | string | null, spb: number, entry: boolean) {
    void count; void spb; void entry;
    const ctx = this.context, o = ctx.createOscillator(), g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = tier >= 3 ? 1650 : 1180;
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(tier >= 3 ? 0.32 : 0.2, at + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0008, at + 0.05);
    o.connect(g); g.connect(this.out); o.start(at); o.stop(at + 0.06);
  }

  silence() {
    const t = this.context.currentTime;
    this.voices.forEach(v => { if (v.end > t) v.stop(t); });
    this.voices.clear();
  }
}
